import {
  AirHandlerTypeCodeType,
  ConcealedDuctLocationType,
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceType,
  PositionType
} from 'data/types';
import { SCALE } from 'utils/3d';
import {
  containingSpaceWallAttachment,
  flushExteriorWallFacing,
  moveInward,
  nearestWallAttachment,
  nearestWallFacing,
  wallFacingYaw,
  wallMountedYaw
} from 'utils/3d-wall-placement';
import { Wall3DFacingType } from 'utils/3d-wall-placement-types';
import { isCeilingMountedAirHandler } from 'utils/air-handler-mounting';
import { getConcealedDuctUnitWorldY } from 'utils/concealed-duct-placement';
import { EMPTY_FOOTPRINT_POLYGONS } from 'utils/footprint-polygons';

export const EQUIPMENT_GRID_SQUARES = 3;

export const CONDENSER_3D_DIMENSIONS = {
  width: 0.625,
  height: 1.25,
  depth: 1.25
};

const CONDENSER_WALL_HEIGHT = 2.5;
const CONDENSER_HALF_WIDTH_FT = CONDENSER_3D_DIMENSIONS.width / SCALE / 2;
const CONDENSER_ROOF_INSET_FT =
  EQUIPMENT_GRID_SQUARES / 2 - CONDENSER_HALF_WIDTH_FT;
const AIR_HANDLER_WALL_THICKNESS = 0.1;
const AIR_HANDLER_WALL_CLEARANCE = 0.15;

export type Equipment3DPlacement = {
  x: number;
  y: number;
  z: number;
  rotation: number;
};

export type EquipmentPlacementInput = {
  position: { x: number; y: number };
  placementType?: 'ground' | 'wall' | 'roof';
  heightOffGround?: number;
  airHandlerTypeCode?: AirHandlerTypeCodeType;
  concealedLocation?: ConcealedDuctLocationType | null;
};

export type AirHandlerDimensions = {
  width: number;
  height: number;
  depth: number;
  yPosition: number;
};

export function getAirHandlerDimensions(
  typeCode: AirHandlerTypeCodeType | undefined
): AirHandlerDimensions {
  if (!typeCode) {
    return { width: 2, height: 0.6, depth: 0.5, yPosition: 2.0 };
  }

  if (isCeilingMountedAirHandler(typeCode)) {
    const isConcealedDuct =
      typeCode === 'concealed' || typeCode === 'concealed_high_static';
    return {
      width: 1.2,
      height: isConcealedDuct ? 0.3 : 0.35,
      depth: 1.2,
      yPosition: isConcealedDuct ? 2.4 : 2.45
    };
  }

  if (typeCode === 'heatingConsole') {
    return { width: 2, height: 0.6, depth: 0.5, yPosition: 0.5 };
  }

  if (typeCode === 'console') {
    return { width: 2, height: 0.85, depth: 0.3, yPosition: 0.625 };
  }

  return { width: 2, height: 0.6, depth: 0.5, yPosition: 2.0 };
}

function objectCenterFeet(object: EquipmentPlacementInput): {
  x: number;
  y: number;
} {
  return {
    x: object.position.x + EQUIPMENT_GRID_SQUARES / 2,
    y: object.position.y + EQUIPMENT_GRID_SQUARES / 2
  };
}

function condenserWallFacing(
  center: PositionType,
  placementType: NonNullable<EquipmentPlacementInput['placementType']>,
  spaces: GridSpaceType[],
  footprints: FootprintPolygonsType
): Wall3DFacingType {
  if (placementType === 'roof') {
    return nearestWallFacing(center, CONDENSER_ROOF_INSET_FT, spaces);
  }
  return flushExteriorWallFacing(
    center,
    EQUIPMENT_GRID_SQUARES,
    CONDENSER_HALF_WIDTH_FT,
    spaces,
    footprints
  );
}

export function getCondenser3DPlacement(
  object: EquipmentPlacementInput,
  spaces: GridSpaceType[],
  footprints: FootprintPolygonsType = EMPTY_FOOTPRINT_POLYGONS
): Equipment3DPlacement {
  const { height } = CONDENSER_3D_DIMENSIONS;
  const center = objectCenterFeet(object);
  const placementType = object.placementType ?? 'roof';
  const facing = condenserWallFacing(center, placementType, spaces, footprints);
  const rotation = facing.normal ? wallFacingYaw(facing.normal) : 0;
  const x = facing.point.x * SCALE;
  const z = facing.point.y * SCALE;

  const condenserHeight = object.heightOffGround ?? 0;
  let yPosition: number;
  if (placementType === 'ground') {
    yPosition = condenserHeight + height / 2;
  } else if (placementType === 'wall') {
    yPosition = 1.0 + height / 2;
  } else {
    yPosition = CONDENSER_WALL_HEIGHT + height / 2;
  }

  return {
    x,
    y: yPosition,
    z,
    rotation
  };
}

export function getAirHandler3DPlacement(
  object: EquipmentPlacementInput,
  spaces: GridSpaceType[]
): Equipment3DPlacement {
  const typeCode = object.airHandlerTypeCode;
  const isCeilingMounted = !!typeCode && isCeilingMountedAirHandler(typeCode);
  const { depth, yPosition } = getAirHandlerDimensions(typeCode);
  const center = objectCenterFeet(object);

  const isConcealedDuct =
    typeCode === 'concealed' || typeCode === 'concealed_high_static';
  if (isConcealedDuct) {
    return {
      rotation: 0,
      x: center.x * SCALE,
      y: getConcealedDuctUnitWorldY(object.concealedLocation),
      z: center.y * SCALE
    };
  }

  const attachment = isCeilingMounted
    ? null
    : containingSpaceWallAttachment(center, spaces);

  if (!attachment) {
    return {
      rotation: 0,
      x: center.x * SCALE,
      y: yPosition,
      z: center.y * SCALE
    };
  }

  const wallThicknessGrid = AIR_HANDLER_WALL_THICKNESS / SCALE;
  const inset = wallThicknessGrid + depth / 2 + AIR_HANDLER_WALL_CLEARANCE;
  const placed = moveInward(
    attachment.projection.point,
    attachment.segment.outwardNormal,
    inset
  );

  return {
    rotation: wallMountedYaw(attachment.segment.outwardNormal),
    x: placed.x * SCALE,
    y: yPosition,
    z: placed.y * SCALE
  };
}

export function getElectricalPanel3DPlacement(
  object: EquipmentPlacementInput,
  spaces: GridSpaceType[],
  panelHeight: number
): Equipment3DPlacement {
  const center = objectCenterFeet(object);
  const attachment = nearestWallAttachment(center, spaces);
  return {
    rotation: attachment ? wallFacingYaw(attachment.segment.outwardNormal) : 0,
    x: center.x * SCALE,
    y: panelHeight / 2,
    z: center.y * SCALE
  };
}

export function getEquipment3DPlacement(
  object: GridObjectType,
  spaces: GridSpaceType[],
  footprints: FootprintPolygonsType = EMPTY_FOOTPRINT_POLYGONS
): Equipment3DPlacement {
  if (object.type === 'condenser') {
    return getCondenser3DPlacement(object, spaces, footprints);
  }
  return getAirHandler3DPlacement(object, spaces);
}
